import React, { useEffect, useState } from 'react';
import { Form, Upload, Button, Image, Typography } from 'antd';
import { UploadOutlined, EyeOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop'; // 👈 crop library
import masterService from '../../services/masterService';
import { useNotify } from '../../context/NotificationContext';
import { getImageUrl } from '../../utils/commonFunctions';
import GifCropModal from './GifCropModal';

const { Text } = Typography;

const ImageUpload = ({
  label,
  name,
  width,
  height,
  imageType = 'banners',
  required = false,
  value,
  onChange,
  allowGif = false,
  minGifSize = false
}) => {
  const [imageUrl, setImageUrl] = useState(value || '');
  const [uploading, setUploading] = useState(false);
  const [gifFile, setGifFile] = useState(null);
  const notify = useNotify();

  useEffect(() => {
    setImageUrl(value || '');
  }, [value]);

  // Send file to S3 (no validation — caller must validate)
  const uploadFile = async (file) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('image_type', 'images');
      formData.append('folder_name', imageType);
      formData.append('image', file);

      const response = await masterService.uploadImage(formData);

      if (response.data && response.data.status) {
        const imagePath = response.data.data.s3_key;
        setImageUrl(imagePath);
        onChange && onChange(imagePath);
        notify.success({ message: 'Image uploaded successfully!' });
      } else {
        notify.error({ message: 'Failed to upload image' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      notify.error({ message: 'Failed to upload image' });
    } finally {
      setUploading(false);
    }
  };

  // beforeUpload — runs for cropped images. For GIFs, beforeCrop already
  // intercepted and opened GifCropModal, so we short-circuit here.
  const handleUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      notify.error({ message: 'You can only upload image files!' });
      return Upload.LIST_IGNORE;
    }

    if (file.type === 'image/gif') {
      if (!allowGif) {
        notify.error({ message: 'GIF files are not allowed here!' });
      }
      // GIFs are handled exclusively by the GifCropModal flow
      return Upload.LIST_IGNORE;
    }

    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      notify.error({ message: 'Image must be smaller than 50MB!' });
      return Upload.LIST_IGNORE;
    }

    await uploadFile(file);
    return false; // prevent auto upload
  };

  // Handle image removal

  // Upload props
  const uploadProps = {
    name: 'image',
    showUploadList: false,
    beforeUpload: handleUpload,
    accept: allowGif ? 'image/jpeg,image/png,image/webp,image/gif' : 'image/jpeg,image/png,image/webp',
    disabled: uploading,
  };

  // GIFs need a custom modal — ImgCrop uses canvas which strips animation.
  // Returning false from beforeCrop bypasses ImgCrop; we open our own modal
  // and the file is processed there (so beforeUpload below is not reached).
  const beforeCrop = (file) => {
    if (allowGif && file?.type === 'image/gif') {
      setGifFile(file);
      return false;
    }
    return true;
  };

  const handleGifCropped = async (croppedFile) => {
    setGifFile(null);
    const isLt50M = croppedFile.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      notify.error({ message: 'Image must be smaller than 50MB!' });
      return;
    }
    await uploadFile(croppedFile);
  };

  return (
    <Form.Item
      label={label}
      name={name}
      rules={required ? [{ required: true, message: `Please upload ${label.toLowerCase()}` }] : []}
    >
      <div>
        {/* Form.Item injects its own onChange onto the wrapper above, and the
            hidden file input's native change event bubbles straight into it —
            antd then reads e.target.value and stores "C:\fakepath\<filename>"
            as the field value. That replaced the saved image the moment a file
            was picked: confirming the crop hid it, because the upload overwrote
            the field with the real key, but cancelling left the fake path
            standing, so the preview broke and a save wrote that path to the
            database. The upload reports its result through onChange below, so
            the DOM event is never the value and is stopped here. */}
        <div onChange={(event) => event.stopPropagation()}>
          {/* Crop wrapper around Upload */}
          <ImgCrop
            aspect={width / height}   // 👈 restrict aspect ratio
            rotationSlider
            quality={1}
            beforeCrop={beforeCrop}
          >
            <Upload {...uploadProps}>
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Image'}
              </Button>
            </Upload>
          </ImgCrop>
        </div>

        {/* Image Dimensions Info */}
        <div style={{ marginTop: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px', color: 'rgba(0, 0, 0, 0.65)' }}>
            Recommended size: {width}px (W) × {height}px (H)
            {allowGif ? ' • GIFs supported (animation preserved)' : ''}
          </Text>
        </div>

        {/* Image Preview */}
        {imageUrl && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px', 
              padding: '8px',
              display: 'inline-block',
              backgroundColor: '#ffffff'
            }}>
              <Image
                src={getImageUrl(imageUrl)}
                alt="Preview"
                style={{ 
                  maxWidth: '200px', 
                  maxHeight: '150px',
                  objectFit: 'contain'
                }}
                preview={{
                  mask: <EyeOutlined />
                }}
              />
            </div>
            
          </div>
        )}

        {allowGif && (
          <GifCropModal
            open={!!gifFile}
            file={gifFile}
            aspect={width / height}
            outputSize={minGifSize ? { width, height } : undefined}
            onCancel={() => setGifFile(null)}
            onCropped={handleGifCropped}
          />
        )}
      </div>
    </Form.Item>
  );
};

export default ImageUpload;